import {
  BdArrayUtil,
  OverrideProperty,
  OverridePropertyHelper,
} from '@bindoc-projects/util-domain-lib';
import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';
import type { FilterDetailedText } from '../filter-detailed-text';

export const FILTER_RESOURCE_SPECIALTY_DEPARTMENT = 'specialty-department';

export const BD_VIRTUAL_CLINIC_SPECIALTY_DEPARTMENT_ID_CONDITION_ID =
  'specialtyDepartmentId';
export const FilterConditionVirtualClinicSpecialtyDepartmentIdsPluginType: FilterConditionPluginSpecification<
  string[],
  FilterConditionVirtualClinicSpecialtyDepartmentIdsDto,
  BdVirtualClinicSpecialtyDepartmentIdCondition
> = {
  type: 'specialtyDepartmentIds',
  category: 'case',
  displayName: { de: 'Fachabteilung' },
  sortingIndex: 8,
  occurrence: 'single',
  fromObject(
    obj: FilterConditionVirtualClinicSpecialtyDepartmentIdsDto,
  ): FilterConditionPluginImpl<string[]> {
    return new FilterConditionVirtualClinicSpecialtyDepartmentIdsPlugin(
        obj.specialtyDepartmentIds,
      );
  },
  fromCondition(
    jsonObj: BdVirtualClinicSpecialtyDepartmentIdCondition,
  ): FilterConditionPluginImpl<string[]> {
    return new FilterConditionVirtualClinicSpecialtyDepartmentIdsPlugin(
        jsonObj.includes,
      );
  },
  newInstance(): FilterConditionPluginImpl<string[]> {
    return new FilterConditionVirtualClinicSpecialtyDepartmentIdsPlugin([]);
  },
};

export class FilterConditionVirtualClinicSpecialtyDepartmentIdsPlugin extends FilterConditionPluginImpl<
  string[],
  FilterConditionVirtualClinicSpecialtyDepartmentIdsDto
> {
  public static override Specification: FilterConditionPluginSpecification<
    string[],
    FilterConditionVirtualClinicSpecialtyDepartmentIdsDto,
    BdVirtualClinicSpecialtyDepartmentIdCondition
  > = FilterConditionVirtualClinicSpecialtyDepartmentIdsPluginType;

  constructor(private specialtyDepartmentIds: string[]) {
    super(FilterConditionVirtualClinicSpecialtyDepartmentIdsPluginType.type);
  }

  clone(): FilterConditionVirtualClinicSpecialtyDepartmentIdsPlugin {
    return new FilterConditionVirtualClinicSpecialtyDepartmentIdsPlugin([
      ...this.specialtyDepartmentIds,
    ]);
  }

  override equals(
    other: FilterConditionVirtualClinicSpecialtyDepartmentIdsPlugin,
  ): boolean {
    return (
      super.equals(other) &&
      BdArrayUtil.equals(
        this.specialtyDepartmentIds,
        other.specialtyDepartmentIds,
      )
    );
  }

  override getOverrideProperties(
    tileId: string,
    dependencyVirtualClinicId: string,
  ): OverrideProperty[] {
    return this.specialtyDepartmentIds.map(
      (it) =>
        new OverrideProperty<string>(
          tileId,
          'specialtyDepartmentId',
          it,
          it,
          'possible',
          dependencyVirtualClinicId,
        ),
    );
  }

  override overrideClone(
    override: OverrideProperty[],
  ): FilterConditionVirtualClinicSpecialtyDepartmentIdsPlugin {
    const specialtyDepartmentIds = OverridePropertyHelper.overrideProperties(
      this.specialtyDepartmentIds,
      'specialtyDepartmentId',
      override,
      (value) => value,
    );

    return new FilterConditionVirtualClinicSpecialtyDepartmentIdsPlugin(
      specialtyDepartmentIds,
    );
  }

  override validationToText(): string {
    if (this.specialtyDepartmentIds.length === 0) {
      return 'Mindestens eine Fachabteilung auswählen';
    } else {
      return '';
    }
  }

  toCondition(): BdCondition | null {
    return new BdVirtualClinicSpecialtyDepartmentIdCondition(
        this.specialtyDepartmentIds,
      );
  }

  toJSON(): FilterConditionVirtualClinicSpecialtyDepartmentIdsDto | null {
    return {
      type: this.type,
      specialtyDepartmentIds: this.specialtyDepartmentIds,
    };
  }

  toText(): string | null {
    return this.specialtyDepartmentIds.length === 1
      ? 'eine Fachabteilung'
      : `${this.specialtyDepartmentIds.length} Fachabteilungen`;
  }

  override getExtraChildResources(): Record<string, string[]> {
    if (this.specialtyDepartmentIds.length === 0) return {};
    return {
      [FILTER_RESOURCE_SPECIALTY_DEPARTMENT]: [...this.specialtyDepartmentIds],
    };
  }

  override toDetailedText(
    resolvedNames?: Map<string, string>,
  ): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    const items = this.specialtyDepartmentIds.map(
      (id) => resolvedNames?.get(id) ?? id,
    );
    return { title, items };
  }

  toValue(): string[] {
    return this.specialtyDepartmentIds;
  }
}

export class BdVirtualClinicSpecialtyDepartmentIdCondition extends BdContentCondition<string> {
  constructor(includes: string[] = [], excludes: string[] = []) {
    super(
      BD_VIRTUAL_CLINIC_SPECIALTY_DEPARTMENT_ID_CONDITION_ID,
      includes,
      excludes,
    );
  }
}

interface FilterConditionVirtualClinicSpecialtyDepartmentIdsDto {
  type: string;
  specialtyDepartmentIds: string[];
}
